import type { ReactNode } from "react";

import { resolvePage, tenantAdminPages } from "../navigation.ts";
import type { Session } from "../session.ts";

/**
 * What this console can and cannot do yet, said out loud.
 *
 * Not a placeholder, and on this console not a small table either: `17 WP16 Tenant Admin.dc.html`
 * draws fifteen pages and **six of them had no operation a browser can call** — eleven when this
 * was written, five of which R-39 served and R-40 drew. None has, now, and the console draws four
 * more the design does not: the commissioned analytics boards, recorded as Q-186. Each of the six carries its own measured
 * reason in `navigation.ts`, and this page is where an administrator reads them — rather than
 * discovering, one rail item at a time, which parts of the suite are not there.
 *
 * The third column's other sentence — "A screen. The operation and your permissions are both already
 * in place." — says nothing about any page today, because every page the platform serves is drawn.
 * It is kept because it is the state the next served listing puts a page into, and because R-39 and
 * R-40 were two commits apart: for the length of one, five pages were in exactly that state and this
 * was the only place that said so.
 *
 * It is the right page for them for the same reason the rail's hidden-page count excludes them: a
 * page blocked by the platform is not a page an administrator can be given permission to see, so
 * telling them "ask whoever administers your roles" would send them to somebody who can do nothing.
 *
 * A stub pretending to be a pricing board would teach an administrator that the console is
 * unreliable. This teaches them something true.
 */

export interface PageOutlookProps {
  readonly session: Session;
}

export function PageOutlook({ session }: PageOutlookProps): ReactNode {
  const rows = tenantAdminPages.map((page) => ({
    page,
    resolved: resolvePage(page, session.scopes),
  }));
  const blocked = rows.filter(({ page }) => page.blockedBy !== undefined).length;

  return (
    <section className="page">
      <h1 className="page__title">Console readiness</h1>
      <p className="page__lead">
        {`This console draws ${String(rows.length)} pages and ${String(blocked)} of them have no operation behind them yet. Each row below says what is missing, measured against the published contracts rather than remembered.`}
      </p>

      <table className="outlook">
        <thead>
          <tr>
            <th scope="col">Page</th>
            <th scope="col">Your access</th>
            <th scope="col">Waiting on</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ page, resolved }) => (
            <tr key={page.id}>
              <th scope="row">{page.title}</th>
              <td>
                {resolved.access === "forbidden"
                  ? page.blockedBy === undefined
                    ? "No permission"
                    : "Not available"
                  : resolved.access === "read_only"
                    ? "View only"
                    : "Full"}
              </td>
              <td>
                {page.blockedBy ??
                  "A screen. The operation and your permissions are both already in place."}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
